import { useEffect, useState } from 'react';
import { FileText, Loader2, Check, Sparkles, AlertCircle, ChevronDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BusinessKnowledgeCardProps {
  value: string;
  onSave: (text: string) => Promise<unknown>;
  isSaving?: boolean;
}

export function BusinessKnowledgeCard({ value, onSave, isSaving }: BusinessKnowledgeCardProps) {
  const [text, setText] = useState(value);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => { setText(value); }, [value]);

  const dirty = text !== value;

  const handleSave = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await onSave(text);
      setSavedAt(Date.now());
      toast.success('Business knowledge saved & indexed for the AI');
    } catch {
      toast.error('Could not save business knowledge');
    }
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
      {/* Pod Header (Clickable to expand/collapse) */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2 cursor-pointer select-none hover:bg-slate-100/80 dark:hover:bg-slate-800/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={cn('h-4 w-4 text-slate-500 dark:text-slate-400 transition-transform duration-200', isOpen && 'rotate-180')} />
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
            <FileText className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Business Knowledge & Policies
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/80 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-400">
            Prompt Context
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[11px] font-medium">
            {savedAt && !dirty ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-semibold">
                <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /> Saved & indexed
              </span>
            ) : dirty ? (
              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-semibold">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" /> Unsaved changes
              </span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">Up to date</span>
            )}
          </span>

          <Button
            type="button"
            onClick={handleSave}
            disabled={!dirty || isSaving}
            size="sm"
            className="h-7 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold shadow-xs disabled:opacity-50 transition-colors gap-1.5"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-emerald-200" />}
            {isSaving ? 'Saving…' : 'Save & Index'}
          </Button>
        </div>
      </div>

      {/* Collapsible Body */}
      {isOpen && (
        <div className="p-4 bg-white/70 dark:bg-slate-950/40 space-y-2.5 animate-in fade-in-50 duration-200">
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Paste your shop information as natural documents &mdash; about us, delivery charges, payment options, return policies. The AI assistant embeds this to answer customer inquiries directly.
          </p>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={7}
            placeholder={`e.g.\nAbout: We are a Dhaka-based fashion store, est. 2022.\nDelivery: ৳60 inside Dhaka (1–2 days), ৳120 outside (3–5 days). Free over ৳3000.\nPayment: bKash, Nagad, Rocket, Cash on Delivery.\nReturns: 3-day exchange on unused items with tags.`}
            className="w-full resize-y rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 px-3.5 py-2.5 text-xs font-mono text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-2xs leading-relaxed"
          />
        </div>
      )}
    </Card>
  );
}

export default BusinessKnowledgeCard;
